import { AfterViewInit, ChangeDetectorRef, Component, inject, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { Severity } from '../../../types/app-types';
import { FieldModel } from '../../../view-model/field-model';
import { GroupModel } from '../../../view-model/group-model';
import { SectionModel } from '../../../view-model/section-model';
import { RepeatableGroupModel } from '../../../view-model/repeatable-group-model';
import { IdGenerationService } from '../../../services/id-generation.service';
import { ModelListenerService } from '../../../services/view-model/model-listener.service';
import { StateService } from '../../../services/state.service';
import { FormControl } from '@angular/forms';
import { UserSettings } from '../../../store/user-settings/user-settings.action';

@Component({
  template: '',
})
export abstract class AbstractFormComponent implements OnDestroy, AfterViewInit {
  model: FieldModel<any> | GroupModel | RepeatableGroupModel | SectionModel;
  subscription = new Subscription();
  htmlId: string;
  userSettings: UserSettings;
  formControls: { control: FormControl }[] = [];

  protected modelListenerService: ModelListenerService = inject(ModelListenerService);
  protected cdRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  protected idGenerationService: IdGenerationService = inject(IdGenerationService);
  protected stateService: StateService = inject(StateService);

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngAfterViewInit() {
    if (this.model.focus) {
      this.focus();
    }
  }

  /**
   * Sets the Model of the Component and registers ChangeListeners on it.
   */
  public initWithModel(model: FieldModel<any> | GroupModel | RepeatableGroupModel | SectionModel) {
    this.model = model;
    this.subscription.add(this.model.modelChangedEmitter.subscribe(() => this.onModelChange()));
    this.subscription.add(this.model.updateViewEmitter.subscribe(() => this.cdRef.markForCheck()));
    this.subscription.add(
      this.stateService.getUserSettings().subscribe(this.userSettingsChanged.bind(this))
    );
    this.htmlId = this.idGenerationService.getHtmlId();
  }

  hasInputError(): boolean {
    return this.model.validationNotifications.some(
      notification => notification.severity === Severity.ERROR
    );
  }

  /**
   * Called after the Value of the Form Component has been changed.
   */
  onInputValueChanged() {
    if (this.model instanceof FieldModel) {
      this.modelListenerService.onModelValueChange(this.model);
    }
  }

  protected userSettingsChanged(userSettings: UserSettings) {
    this.userSettings = userSettings;
    this.calculateReadonlyFlagForAllControls();
  }

  private calculateReadonlyFlagForAllControls() {
    this.formControls.forEach(item => this.calculateReadonlyFlagForControl(item.control));
    this.cdRef.markForCheck();
  }

  private calculateReadonlyFlagForControl(control: FormControl) {
    if (!this.userSettings.superEditMode && this.model.isReadonly) {
      control.disable({ emitEvent: false });
    } else {
      control.enable({ emitEvent: false });
    }
  }

  /**
   * Registers a Control for it to be updated when the readonly state should be changed.
   * @param control target formControl
   * @param model source model for formControl
   * @protected
   */
  protected registerControlForReadonlyChanges(control: FormControl) {
    this.formControls.push({ control });
    this.calculateReadonlyFlagForControl(control);
  }

  protected onModelChange(): void {
    if (this.model.focus) {
      this.focus();
    }

    this.calculateReadonlyFlagForAllControls();
    this.cdRef.markForCheck();
  }

  protected isInputHidden() {
    return this.model.isInputHidden && !this.userSettings.superEditMode;
  }

  /**
   * bt-702(a)-notice & BT-702(b)-notice are "Erfassungssprachen"
   * Fields are only displayed above in the header, not in the eforms metadata.
   * @protected
   */
  protected showField() {
    if (
      this.model.noticeNode?.id === 'BT-702(a)-notice' ||
      this.model.noticeNode?.id === 'BT-702(b)-notice'
    ) {
      return false;
    } else {
      return !this.model.isInputHidden || this.userSettings.showHiddenFields;
    }
  }

  protected focus(input?: HTMLElement): void {
    input = input ?? document.getElementById(this.htmlId);

    setTimeout(() => {
      if (this.model.isReadonly) {
        input.scrollIntoView({
          block: 'start',
          inline: 'nearest',
        });
      } else {
        input?.focus();
      }
    });
    this.model.focus = false;
  }
}
